import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLogoTheme } from '../context/LogoThemeContext';

export default function Header() {
  const { currentPreset, headerBgColor } = useLogoTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [forlobDropdownOpen, setForlobDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const forlobDropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (forlobDropdownRef.current && !forlobDropdownRef.current.contains(event.target)) {
        setForlobDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setForlobDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isForlobActive =
    location.pathname.startsWith('/forlob') ||
    location.pathname === '/forbundet-fra-hjertet' ||
    location.pathname === '/1-1-forlob' ||
    location.pathname === '/lyt-fra-hjertet' ||
    location.pathname === '/forbundet-med-dit-horoskop' ||
    location.pathname === '/astrologi-fra-hjertet' ||
    location.pathname === '/astrologisk-1-1-session';

  return (
    <header
      style={{
        backgroundColor: scrolled ? headerBgColor : 'transparent',
        borderColor: scrolled ? '#E8DFD5' : 'transparent'
      }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-md shadow-xs py-2.5 border-b' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group text-[#2E2A27] focus:outline-hidden -translate-x-[5px]"
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/height=240,quality=95,format=auto/${currentPreset.url.replace('https://assets.auxodev.dk/', '')}`}
              alt="Lenea Evolinee"
              className="h-20 sm:h-24 lg:h-28 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (Visible >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 py-1 relative ${
                isActive('/') && location.pathname === '/'
                  ? 'text-[#98866C] font-semibold'
                  : 'text-[#2E2A27] hover:text-[#98866C]'
              }`}
            >
              Forside
              {isActive('/') && location.pathname === '/' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#98866C] rounded-full" />
              )}
            </Link>

            <Link
              to="/om-zentai"
              className={`text-sm font-medium transition-colors duration-200 py-1 relative ${
                isActive('/om-zentai')
                  ? 'text-[#98866C] font-semibold'
                  : 'text-[#2E2A27] hover:text-[#98866C]'
              }`}
            >
              Om Lenea Evolinee
              {isActive('/om-zentai') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#98866C] rounded-full" />
              )}
            </Link>

            {/* Dropdown Forløb */}
            <div className="relative" ref={forlobDropdownRef}>
              <div className="flex items-center">
                <Link
                  to="/forlob"
                  className={`text-sm font-medium transition-colors duration-200 py-1 pr-1 relative ${
                    isForlobActive ? 'text-[#98866C] font-semibold' : 'text-[#2E2A27] hover:text-[#98866C]'
                  }`}
                >
                  Forløb
                  {isForlobActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#98866C] rounded-full" />
                  )}
                </Link>
                <button
                  type="button"
                  onClick={() => setForlobDropdownOpen(!forlobDropdownOpen)}
                  onMouseEnter={() => setForlobDropdownOpen(true)}
                  className={`p-1 cursor-pointer transition-colors ${
                    isForlobActive ? 'text-[#98866C]' : 'text-[#2E2A27] hover:text-[#98866C]'
                  }`}
                  aria-label="Åbn forløbsmenu"
                  aria-expanded={forlobDropdownOpen}
                >
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                      forlobDropdownOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>

              {/* Dropdown Menu */}
              {forlobDropdownOpen && (
                <div
                  onMouseLeave={() => setForlobDropdownOpen(false)}
                  className="absolute left-0 mt-2 w-64 rounded-2xl bg-white shadow-lg border border-[#E8DFD5] py-2 z-50 animate-fade-in"
                >
                  <Link
                    to="/forbundet-fra-hjertet"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#2E2A27] hover:bg-[#F7F2EB] hover:text-[#98866C] transition-colors duration-150"
                  >
                    <i className="fa-solid fa-heart text-[#98866C] w-4 text-center" aria-hidden="true" />
                    <span>Forbundet fra Hjertet</span>
                  </Link>
                  <Link
                    to="/1-1-forlob"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#2E2A27] hover:bg-[#F7F2EB] hover:text-[#98866C] transition-colors duration-150"
                  >
                    <i className="fa-solid fa-user text-[#98866C] w-4 text-center" aria-hidden="true" />
                    <span>1:1-forløb</span>
                  </Link>
                  <Link
                    to="/lyt-fra-hjertet"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#2E2A27] hover:bg-[#F7F2EB] hover:text-[#98866C] transition-colors duration-150"
                  >
                    <i className="fa-solid fa-ear-listen text-[#98866C] w-4 text-center" aria-hidden="true" />
                    <span>Lyt – fra Hjertet</span>
                  </Link>
                  <Link
                    to="/forbundet-med-dit-horoskop"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#2E2A27] hover:bg-[#F7F2EB] hover:text-[#98866C] transition-colors duration-150"
                  >
                    <i className="fa-solid fa-sparkles text-[#98866C] w-4 text-center" aria-hidden="true" />
                    <span>Forbundet med dit horoskop</span>
                  </Link>
                  <div className="border-t border-[#E8DFD5] my-1" />
                  <Link
                    to="/forlob"
                    className="flex items-center gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#98866C] hover:bg-[#F7F2EB] transition-colors duration-150"
                  >
                    <span>Se alle forløb →</span>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/kontakt"
              className={`text-sm font-medium transition-colors duration-200 py-1 relative ${
                isActive('/kontakt')
                  ? 'text-[#98866C] font-semibold'
                  : 'text-[#2E2A27] hover:text-[#98866C]'
              }`}
            >
              Kontakt
              {isActive('/kontakt') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#98866C] rounded-full" />
              )}
            </Link>
          </nav>

          {/* Desktop Right CTA Button */}
          <div className="hidden min-[1200px]:block">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#98866C] text-white text-sm font-semibold shadow-xs hover:bg-[#83735C] transition-all duration-200 cursor-pointer"
            >
              Book samtale
            </Link>
          </div>

          {/* Hamburger Menu Button (Only visible < 1200px) */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="block min-[1200px]:hidden p-2.5 rounded-full text-[#2E2A27] hover:bg-[#E8DFD5] transition-colors duration-200 cursor-pointer"
            aria-label="Åbn navigation"
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileMenuOpen && (
        <div
          style={{
            backgroundColor: headerBgColor,
            borderColor: '#E8DFD5'
          }}
          className="min-[1200px]:hidden border-b px-4 pt-4 pb-6 shadow-md animate-fade-in"
        >
          <nav className="flex flex-col space-y-3">
            <Link
              to="/"
              className={`px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/') && location.pathname === '/'
                  ? 'bg-[#98866C]/20 text-[#2E2A27] font-semibold'
                  : 'text-[#2E2A27] hover:bg-[#F4EFE6]'
              }`}
            >
              Forside
            </Link>

            <Link
              to="/om-zentai"
              className={`px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/om-zentai')
                  ? 'bg-[#98866C]/20 text-[#2E2A27] font-semibold'
                  : 'text-[#2E2A27] hover:bg-[#F4EFE6]'
              }`}
            >
              Om Lenea Evolinee
            </Link>

            <div className="pl-4 pr-2 py-2 border-l-2 border-[#98866C]/50 space-y-2 my-1">
              <Link
                to="/forlob"
                className={`text-xs uppercase tracking-wider font-bold block px-2 ${
                  isForlobActive ? 'text-[#98866C]' : 'text-[#2E2A27]'
                }`}
              >
                Forløb & Fællesskaber
              </Link>
              <Link
                to="/forbundet-fra-hjertet"
                className={`block px-2 py-1.5 rounded-lg text-sm font-medium ${
                  isActive('/forbundet-fra-hjertet') ? 'text-[#98866C] font-bold' : 'text-[#2E2A27]'
                }`}
              >
                • Forbundet fra Hjertet
              </Link>
              <Link
                to="/1-1-forlob"
                className={`block px-2 py-1.5 rounded-lg text-sm font-medium ${
                  isActive('/1-1-forlob') ? 'text-[#98866C] font-bold' : 'text-[#2E2A27]'
                }`}
              >
                • 1:1-forløb
              </Link>
              <Link
                to="/lyt-fra-hjertet"
                className={`block px-2 py-1.5 rounded-lg text-sm font-medium ${
                  isActive('/lyt-fra-hjertet') ? 'text-[#98866C] font-bold' : 'text-[#2E2A27]'
                }`}
              >
                • Lyt – fra Hjertet
              </Link>
              <Link
                to="/forbundet-med-dit-horoskop"
                className={`block px-2 py-1.5 rounded-lg text-sm font-medium ${
                  isActive('/forbundet-med-dit-horoskop') || isActive('/astrologi-fra-hjertet') ? 'text-[#98866C] font-bold' : 'text-[#2E2A27]'
                }`}
              >
                • Forbundet med dit horoskop (1:1)
              </Link>
            </div>

            <Link
              to="/kontakt"
              className={`px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/kontakt')
                  ? 'bg-[#98866C]/20 text-[#2E2A27] font-semibold'
                  : 'text-[#2E2A27] hover:bg-[#F4EFE6]'
              }`}
            >
              Kontakt
            </Link>

            <div className="pt-2">
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#98866C] text-white text-base font-semibold shadow-xs hover:bg-[#83735C] transition-all"
              >
                Book en samtale
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
